'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { bookingService } from '../../services/bookingService';
import { AvailabilitySlot } from '../../types/models';

export default function BookingPage() {
  const router = useRouter();
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Booking form state
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
  const [bookingForm, setBookingForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    notes: '',
    location: 'Online', // Add location field
  });

  useEffect(() => {
    loadAvailableSlots();
  }, []);

  // Load user data from localStorage if available
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const savedUserData = localStorage.getItem('user_data');
        if (savedUserData) {
          const userData = JSON.parse(savedUserData);
          setBookingForm((prevForm) => ({
            ...prevForm,
            clientName: userData.name || '',
            clientEmail: userData.email || '',
            clientPhone: userData.phone || '',
          }));
        }
      } catch (error) {
        console.error('Error loading user data from localStorage:', error);
      }
    }
  }, []);

  const loadAvailableSlots = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error } = await bookingService.getAvailableSlots();
      
      if (error) {
        setError('Nepodařilo se načíst dostupné termíny');
        return;
      }
      
      setSlots(data ?? []);
    } catch (err) {
      setError('Nepodařilo se načíst dostupné termíny');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSlot = (slot: AvailabilitySlot) => {
    setSelectedSlot(slot);
    setShowBookingForm(true);
    setError('');
    setSuccess('');
  };

  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedSlot) return;

    // Validate form - phone is now required
    if (!bookingForm.clientName || !bookingForm.clientEmail || !bookingForm.clientPhone) {
      setError('Prosím, vyplňte všechna povinná pole (jméno, e-mail, telefon)');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(bookingForm.clientEmail)) {
      setError('Prosím, zadejte platnou e-mailovou adresu');
      return;
    }

    try {
      const { error } = await bookingService.createBooking({
        clientEmail: bookingForm.clientEmail,
        clientName: bookingForm.clientName,
        clientPhone: bookingForm.clientPhone,
        slotId: selectedSlot.id,
        notes: bookingForm.notes,
        location: bookingForm.location, // Include location in booking
      });

      if (error) {
        if (error.message?.includes('duplicate') || error.message?.includes('already')) {
          setError('Tento termín byl právě rezervován. Prosím, vyberte jiný.');
        } else {
          setError(`Nepodařilo se vytvořit rezervaci: ${error.message}`);
        }
        return;
      }

      // Success
      setSuccess('Rezervace byla úspěšně vytvořena! Brzy vás budeme kontaktovat.');
      setShowBookingForm(false);
      setSelectedSlot(null);
      setBookingForm({
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        notes: '',
        location: 'Online', // Reset location to default
      });
      
      // Save user data for post-booking page
      if (typeof window !== 'undefined') {
        localStorage.setItem('user_data', JSON.stringify({
          name: bookingForm.clientName,
          email: bookingForm.clientEmail,
          phone: bookingForm.clientPhone,
        }));
      }

      // Redirect to post-booking confirmation page
      router.push('/booking-confirmation');
      
      // Reload slots to reflect changes
      await loadAvailableSlots();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nepodařilo se vytvořit rezervaci');
    }
  };

  const handleCancelBooking = () => {
    setShowBookingForm(false);
    setSelectedSlot(null);
    setError('');
    setBookingForm({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      notes: '',
      location: 'Online', // Reset location to default
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Rezervace konzultace</h1>
            <button
              onClick={() => router.push('/')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Zpět na homepage
            </button>
          </div>
          <p className="mt-2 text-gray-600">Vyberte si vhodný termín pro konzultaci</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{success}</span>
            <button
              onClick={() => setSuccess('')}
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
            >
              <span className="text-xl">&times;</span>
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <button
              onClick={() => setError('')}
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
            >
              <span className="text-xl">&times;</span>
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Načítání dostupných termínů...</span>
          </div>
        )}

        {/* Booking Form */}
        {showBookingForm && selectedSlot && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Rezervovat termín</h2>
            <div className="mb-6 p-4 bg-blue-50 rounded-md">
              <p className="font-medium text-blue-900">
                {formatDate(selectedSlot.slotDate)}
              </p>
              <p className="text-blue-700">{selectedSlot.slotTime} (45 minut)</p>
            </div>

            <form onSubmit={handleSubmitBooking} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jméno a příjmení *
                </label>
                <input
                  type="text"
                  required
                  value={bookingForm.clientName}
                  onChange={(e) => setBookingForm({ ...bookingForm, clientName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Jan Novák"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  required
                  value={bookingForm.clientEmail}
                  onChange={(e) => setBookingForm({ ...bookingForm, clientEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="jan.novak@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon *
                </label>
                <input
                  type="tel"
                  required
                  value={bookingForm.clientPhone}
                  onChange={(e) => setBookingForm({ ...bookingForm, clientPhone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+420 123 456 789"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Místo konání *
                </label>
                <select
                  required
                  value={bookingForm.location}
                  onChange={(e) => setBookingForm({ ...bookingForm, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Online">Online</option>
                  <option value="Osobně (V Parku 2335/20, Praha)">Osobně (V Parku 2335/20, Praha)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Poznámka (volitelné)
                </label>
                <textarea
                  rows={3}
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Zde můžete napsat dodatečné informace..."
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium"
                >
                  Potvrdit rezervaci
                </button>
                <button
                  type="button"
                  onClick={handleCancelBooking}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 font-medium"
                >
                  Zrušit
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Available Slots */}
        {!loading && !showBookingForm && (
          <>
            {slots.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">Žádné dostupné termíny</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Momentálně nejsou k dispozici žádné volné termíny. Prosím, zkuste to později.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {slots.map((slot) => (
                  <div
                    key={slot.id}
                    className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 cursor-pointer"
                    onClick={() => handleSelectSlot(slot)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {formatDate(slot.slotDate)}
                        </h3>
                        <p className="text-2xl font-bold text-blue-600 mt-1">{slot.slotTime}</p>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Volný
                      </span>
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Délka:</span> 45 minut
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectSlot(slot);
                        }}
                        className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium transition-colors duration-200"
                      >
                        Rezervovat →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}